import i18n from "../i18n";
import RewardsCard from "@/components/RewardsCard";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import LegacyBanner from "@/components/brand/LegacyBanner";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Activity, AlertTriangle, Boxes, CalendarDays, CircleDollarSign, Leaf, PawPrint, RefreshCw, Sparkles, TrendingDown, TrendingUp, Users, WalletCards } from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const PIE_COLORS = ["#2D6A4F", "#A95C3E", "#2A9D8F", "#E9C46A", "#264653", "#E76F51", "#4D908E", "#577590"];
const PERIOD_OPTIONS = [
    { key: "today", label: "Today" },
    { key: "week", label: "This Week" },
    { key: "month", label: "This Month" },
    { key: "year", label: "This Year" },
];
const FARM_TIMEZONE = "Asia/Manila";
// The executive endpoint intentionally reconciles several live farm modules and
// can take a few seconds. Bound every dashboard request so a stalled network or
// database call reaches the existing Retry state instead of leaving the loading
// skeleton on screen forever.
const DASHBOARD_REQUEST_TIMEOUT_MS = 20000;
const DASHBOARD_REQUEST_CONFIG = { timeout: DASHBOARD_REQUEST_TIMEOUT_MS };

const KPI_DEFS = [
    { key: "total_animals", label: "Total Animals", type: "number" },
    { key: "active_animals", label: "Active Animals", type: "number" },
    { key: "market_ready_animals", label: "Market-Ready Animals", type: "number" },
    { key: "pregnant_animals", label: "Pregnant Animals", type: "number" },
    { key: "births_this_month", label: "Births This Month", type: "number" },
    { key: "mortality_this_month", label: "Mortality This Month", type: "number" },
    { key: "feed_inventory_count", label: "Feed Stock Count", type: "number" },
    { key: "inventory_value", label: "Inventory Value", type: "money" },
    { key: "monthly_income", label: "Monthly Income", type: "money" },
    { key: "monthly_expenses", label: "Monthly Expenses", type: "money" },
    { key: "monthly_profit", label: "Monthly Profit", type: "money" },
    { key: "present_employees_today", label: "Present Employees Today", type: "number" },
];

const KPI_VISIBILITY_ALIASES = {
    total_animals: ["total_animals"],
    active_animals: ["total_animals"],
    market_ready_animals: ["market_ready"],
    pregnant_animals: ["pregnant_animals", "active_pregnancies"],
    births_this_month: ["farrowed_today", "farrowing_records_this_month"],
    mortality_this_month: ["mortality"],
    feed_inventory_count: ["feed_stock"],
    inventory_value: ["total_inventory_value"],
    monthly_income: ["income_this_month"],
    monthly_expenses: ["expenses_this_month"],
    monthly_profit: ["profit_this_month"],
    present_employees_today: ["present_employees_today"],
};

let DASHBOARD_FARM_CACHE = null;
let DASHBOARD_CARDS_CACHE = null;
let DASHBOARD_COMMAND_CACHE = null;
let DASHBOARD_STATIC_INFLIGHT = null;
const DASHBOARD_EXEC_INFLIGHT = new Map();

async function fetchDashboardStatic() {
    if (DASHBOARD_FARM_CACHE && DASHBOARD_CARDS_CACHE && DASHBOARD_COMMAND_CACHE) {
        return { farm: DASHBOARD_FARM_CACHE, cards: DASHBOARD_CARDS_CACHE, command: DASHBOARD_COMMAND_CACHE };
    }
    if (DASHBOARD_STATIC_INFLIGHT) {
        return DASHBOARD_STATIC_INFLIGHT;
    }
    DASHBOARD_STATIC_INFLIGHT = Promise.all([
        api.get("/farm", DASHBOARD_REQUEST_CONFIG),
        api.get("/dashboard-cards", DASHBOARD_REQUEST_CONFIG).catch(() => ({ data: [] })),
        api.get("/crops/summary", DASHBOARD_REQUEST_CONFIG).catch(() => ({ data: { kpis: {} } })),
        api.get("/sales-dashboard", DASHBOARD_REQUEST_CONFIG).catch(() => ({ data: { kpis: {} } })),
        api.get("/inventory-items", DASHBOARD_REQUEST_CONFIG).catch(() => ({ data: [] })),
    ]).then(([farmRes, cardsRes, cropsRes, salesRes, inventoryRes]) => {
        DASHBOARD_FARM_CACHE = farmRes.data || { name: "Farm", currency: "PHP" };
        DASHBOARD_CARDS_CACHE = Array.isArray(cardsRes.data) ? cardsRes.data : [];
        DASHBOARD_COMMAND_CACHE = {
            totalCrops: Number(cropsRes.data?.kpis?.total_plots || 0),
            todaySales: Number(salesRes.data?.kpis?.today_sales || 0),
            inventoryItems: Array.isArray(inventoryRes.data) ? inventoryRes.data.length : 0,
        };
        return { farm: DASHBOARD_FARM_CACHE, cards: DASHBOARD_CARDS_CACHE, command: DASHBOARD_COMMAND_CACHE };
    }).finally(() => {
        DASHBOARD_STATIC_INFLIGHT = null;
    });
    return DASHBOARD_STATIC_INFLIGHT;
}

async function fetchDashboardExecutive(periodKey, forceRefresh = false) {
    const key = String(periodKey || "month");
    if (DASHBOARD_EXEC_INFLIGHT.has(key)) {
        return DASHBOARD_EXEC_INFLIGHT.get(key);
    }
    const request = api.get(`/dashboard/executive?period=${encodeURIComponent(key)}`, DASHBOARD_REQUEST_CONFIG).then((res) => {
        return res.data || null;
    }).catch((error) => {
        if (error?.response?.status !== 403) throw error;
        // A dashboard-capable role must never be trapped on an error/loading
        // screen because one analytics scope is unavailable.  This deliberately
        // contains no privileged finance/payroll data; the surrounding page can
        // still render the already-authorized farm/module summary.
        return {
            scope: "role_fallback",
            period: { selected: key },
            kpis: {},
            livestock: {},
            inventory: {},
            attendance_payroll: {},
            alerts_reminders: {},
        };
    }).finally(() => {
        DASHBOARD_EXEC_INFLIGHT.delete(key);
    });
    DASHBOARD_EXEC_INFLIGHT.set(key, request);
    return request;
}

function SectionCard({ title, right, children, testid }) {
    return (
        <Card className="p-5 sm:p-6" data-testid={testid}>
            <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="text-lg sm:text-xl font-semibold">{title}</h2>
                {right || null}
            </div>
            {children}
        </Card>
    );
}

function KpiCard({ label, value, sub, tone = "default" }) {
    const toneClass =
        tone === "good"
            ? "bg-primary/5 border-primary/20"
            : tone === "bad"
                ? "bg-destructive/5 border-destructive/20"
                : "";
    return (
        <Card className={`p-4 border rounded-2xl ${toneClass}`}>
            <div className="tiny-label">{label}</div>
            <div className="text-2xl sm:text-3xl font-bold mt-2 leading-tight break-words">{value}</div>
            {sub ? <div className="text-xs text-muted-foreground mt-1">{sub}</div> : null}
        </Card>
    );
}

function EmptyState({ text }) {
    return <div className="text-sm text-muted-foreground py-6 text-center">{text}</div>;
}

function ChartViewport({ height = 220, children }) {
    const hostRef = useRef(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const node = hostRef.current;
        if (!node) return undefined;

        const update = () => {
            const rect = node.getBoundingClientRect();
            setReady(rect.width > 0 && rect.height > 0);
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(node);
        return () => observer.disconnect();
    }, [height]);

    return (
        <div ref={hostRef} style={{ width: "100%", minWidth: 0, height, minHeight: height }}>
            {ready ? children : null}
        </div>
    );
}

export default function DashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [farm, setFarm] = useState({ name: "Farm", currency: "PHP" });
    const [period, setPeriod] = useState("month");
    const [dashboard, setDashboard] = useState(null);
    const [cards, setCards] = useState([]);
    const [commandSummary, setCommandSummary] = useState({ totalCrops: 0, todaySales: 0, inventoryItems: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [aiStatus, setAiStatus] = useState(null);
    const staticLoadedRef = useRef(false);
    const lastRevalidationRef = useRef(0);

    const load = useCallback(async (nextPeriod, options = {}) => {
        const periodKey = String(nextPeriod || "month");
        const forceRefresh = Boolean(options.forceRefresh);
        setLoading(true);
        setError("");
        try {
            const [{ farm: farmData, cards: cardsData, command }, dashData] = await Promise.all([
                fetchDashboardStatic(),
                fetchDashboardExecutive(periodKey, forceRefresh),
            ]);

            if (!staticLoadedRef.current) {
                staticLoadedRef.current = true;
            }
            setFarm(farmData || { name: "Farm", currency: "PHP" });
            setCards(Array.isArray(cardsData) ? cardsData : []);
            setCommandSummary(command || { totalCrops: 0, todaySales: 0, inventoryItems: 0 });
            setDashboard(dashData || null);
        } catch (err) {
            setError("Unable to load executive dashboard data.");
            setDashboard(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        lastRevalidationRef.current = Date.now();
        load(period);
    }, [period, load]);

    useEffect(() => {
        api.get("/ai/status").then(({ data }) => setAiStatus(data)).catch(() => setAiStatus(null));
    }, []);

    // A backend restart can leave an already-open dashboard displaying a
    // previous snapshot. Revalidate when the owner returns to the tab, while
    // throttling focus/visibility event pairs and avoiding background polling.
    useEffect(() => {
        const revalidateWhenVisible = () => {
            if (document.visibilityState !== "visible") return;
            const now = Date.now();
            if (now - lastRevalidationRef.current < 5000) return;
            lastRevalidationRef.current = now;
            load(period, { forceRefresh: true });
        };
        window.addEventListener("focus", revalidateWhenVisible);
        document.addEventListener("visibilitychange", revalidateWhenVisible);
        return () => {
            window.removeEventListener("focus", revalidateWhenVisible);
            document.removeEventListener("visibilitychange", revalidateWhenVisible);
        };
    }, [load, period]);

    const visibleMetricKeys = useMemo(() => {
        const keys = new Set();
        for (const card of cards || []) {
            if (card && card.metric_key) keys.add(String(card.metric_key));
        }
        return keys;
    }, [cards]);

    const isMetricVisible = useCallback((kpiKey) => {
        if (!visibleMetricKeys.size) return true;
        const aliases = KPI_VISIBILITY_ALIASES[kpiKey] || [kpiKey];
        return aliases.some((key) => visibleMetricKeys.has(key));
    }, [visibleMetricKeys]);

    const money = (v) => formatCurrency(Number(v || 0), farm?.currency || "PHP");
    const number = (v) => formatNumber(Number(v || 0));
    const periodLabel = PERIOD_OPTIONS.find((option) => option.key === period)?.label || "This Month";

    if (loading) {
        return (
            <div className="space-y-4" data-testid="dashboard-page">
                <div>
                    <div className="tiny-label">{farm?.name || "Farm"}</div>
                    <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2">{i18n.t("batch3.d87f47b47e4d")}</h1>
                    <p className="text-muted-foreground mt-2 text-lg">{i18n.t("batch3.e34523b36c1d")}</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                    {Array.from({ length: 12 }).map((_, idx) => (
                        <Card key={idx} className="h-24 animate-pulse bg-muted/40" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4" data-testid="dashboard-page">
                <div>
                    <div className="tiny-label">{farm?.name || "Farm"}</div>
                    <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2">{i18n.t("batch3.d87f47b47e4d")}</h1>
                </div>
                <Card className="p-8 border-destructive/40 bg-destructive/5">
                    <div className="text-destructive font-semibold">{error}</div>
                    <Button className="mt-4" onClick={() => load(period, { forceRefresh: true })}><RefreshCw className="w-4 h-4 mr-2" />{i18n.t("batch3.9f5cd8a2e880")}</Button>
                </Card>
            </div>
        );
    }

    if (!dashboard) {
        return (
            <div className="space-y-4" data-testid="dashboard-page">
                <div>
                    <div className="tiny-label">{farm?.name || "Farm"}</div>
                    <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2">{i18n.t("batch3.d87f47b47e4d")}</h1>
                </div>
                <Card className="p-8 text-center">
                    <div className="text-muted-foreground">{i18n.t("batch3.3ed8fbe89a9e")}</div>
                </Card>
            </div>
        );
    }

    const kpis = dashboard.kpis || {};
    const livestock = dashboard.livestock || {};
    const financial = dashboard.financial || {};
    const inventory = dashboard.inventory || {};
    const attendance = dashboard.attendance_payroll || {};
    const alerts = dashboard.alerts_reminders || {};

    const kpiCards = KPI_DEFS.filter((kpi) => isMetricVisible(kpi.key));

    const lastUpdatedText = dashboard.last_updated
        ? new Date(dashboard.last_updated).toLocaleString("en-PH", { timeZone: FARM_TIMEZONE })
        : "Unknown";
    const dashboardInsightAvailable = Boolean(
        aiStatus?.enabled && aiStatus?.available && aiStatus?.capabilities?.includes("farm_summary")
    );
    const commandKpis = [
        { label: "Total Livestock", value: number(kpis.total_animals || 0), icon: PawPrint },
        { label: "Total Crops", value: number(commandSummary.totalCrops), icon: Leaf },
        { label: "Inventory Items", value: number(commandSummary.inventoryItems), icon: Boxes },
        { label: "Today's Sales", value: money(commandSummary.todaySales), icon: CircleDollarSign },
        { label: "Expenses", value: money(kpis.monthly_expenses || 0), icon: WalletCards },
        { label: "Net Income", value: money(kpis.monthly_profit || 0), icon: TrendingUp },
    ];

    return (
        <div className="space-y-5 aj-dashboard-page" data-testid="dashboard-page">
            <RewardsCard owner />
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <div className="tiny-label text-aj-gold">{i18n.t("batch3.a345f2e5b6e6")}</div>
                    <div className="text-sm font-semibold text-primary mt-2">{i18n.t("batch3.d87f47b47e4d")}</div>
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{i18n.t("batch3.b1ffeaea78c8")} {user?.name || "Farm Owner"}</h1>
                    <p className="text-muted-foreground mt-2 text-base sm:text-lg">{i18n.t("batch3.e32ed0097417")}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {PERIOD_OPTIONS.map((option) => (
                        <Button
                            key={option.key}
                            size="sm"
                            variant={period === option.key ? "default" : "outline"}
                            onClick={() => setPeriod(option.key)}
                            data-testid={`dashboard-period-${option.key}`}
                        >
                            {option.label}
                        </Button>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => load(period, { forceRefresh: true })} data-testid="dashboard-refresh-btn">
                        <RefreshCw className="w-4 h-4 mr-2" />{i18n.t("batch3.56e3badc4e6c")}
                    </Button>
                    {dashboardInsightAvailable ? (
                        <Button size="sm" variant="outline" onClick={() => navigate(`/ai-katulong?capability=farm_summary&prompt=${encodeURIComponent("Explain my dashboard for the selected period: " + period)}`)} data-testid="dashboard-ai-insight-btn">
                            <Sparkles className="w-4 h-4 mr-2" />{i18n.t("ai_katulong.explain_dashboard")}
                        </Button>
                    ) : null}
                </div>
            </div>

            <Card className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                    <div className="text-muted-foreground">
                        <span className="font-medium text-foreground">{i18n.t("batch3.f52767eee809")}</span> {lastUpdatedText}
                    </div>
                    <div className="text-muted-foreground">
                        <span className="font-medium text-foreground">{i18n.t("batch3.b5674a023931")}</span>{" "}
                        {dashboard?.filters?.location_supported ? "Supported" : "Not supported in this data model"}
                    </div>
                </div>
            </Card>

            <section aria-labelledby="command-kpis-title" data-testid="dashboard-command-kpis">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <h2 id="command-kpis-title" className="text-lg font-semibold">{i18n.t("batch3.b58398a70de3")}</h2>
                    <div className="tiny-label">{i18n.t("batch3.330450b7aa66")}</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                    {commandKpis.map(({ label, value, icon: Icon }) => (
                        <Card key={label} className="p-4 min-h-[112px] flex flex-col justify-between border-aj-gold/20">
                            <div className="flex items-center justify-between gap-3">
                                <div className="tiny-label">{label}</div>
                                <Icon className="w-5 h-5 text-aj-gold" aria-hidden="true" />
                            </div>
                            <div className="text-2xl font-bold mt-4">{value}</div>
                        </Card>
                    ))}
                </div>
            </section>

            <div className="grid grid-cols-1 xl:grid-cols-[1.55fr_0.85fr] gap-4">
                <SectionCard title={i18n.t("batch3.94efd3f5de9c")} testid="dashboard-farm-overview">
                    <ChartViewport height={250}>
                        <ResponsiveContainer width="100%" height={250} minWidth={0}>
                            <BarChart data={financial.profit_trend || []}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.22} />
                                <XAxis dataKey="period" fontSize={10} />
                                <YAxis fontSize={10} />
                                <Tooltip formatter={(value) => money(value)} />
                                <Legend />
                                <Bar dataKey="income" fill="#D6AA4A" radius={[6, 6, 0, 0]} />
                                <Bar dataKey="expense" fill="#A95C3E" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartViewport>
                    {!(financial.profit_trend || []).length ? <EmptyState text="No financial activity in the selected period." /> : null}
                </SectionCard>

                <SectionCard
                    title={i18n.t("batch3.8da7a1d95a5f")}
                    right={<div className="text-xs text-muted-foreground">{number((alerts.counts || {}).total || 0)} {i18n.t("batch3.2bb6b986c5d6")}</div>}
                    testid="dashboard-task-reminders"
                >
                    <div className="space-y-1">
                        {(alerts.top_alerts || []).slice(0, 6).map((alert) => (
                            <div key={alert.id} className="flex items-start gap-3 py-2.5 border-b border-aj-gold/10 last:border-0">
                                <AlertTriangle className="w-4 h-4 text-aj-gold mt-0.5 shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold truncate">{alert.title || "Farm reminder"}</div>
                                    <div className="text-xs text-muted-foreground truncate">{alert.detail || "No additional details"}</div>
                                </div>
                            </div>
                        ))}
                        {!(alerts.top_alerts || []).length ? <EmptyState text="No active tasks or reminders." /> : null}
                    </div>
                </SectionCard>
            </div>

            <div className="flex items-center gap-3 pt-1">
                <div className="h-px flex-1 bg-aj-gold/20" />
                <div className="tiny-label text-aj-gold">{i18n.t("batch3.04e24bb92c9c")}</div>
                <div className="h-px flex-1 bg-aj-gold/20" />
            </div>

            <SectionCard title={i18n.t("batch3.4b29c42c0d21")} testid="dashboard-kpis-section">
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                    {kpiCards.map((kpi) => {
                        const raw = Number(kpis[kpi.key] || 0);
                        const value = kpi.type === "money" ? money(raw) : number(raw);
                        const label = ["monthly_income", "monthly_expenses", "monthly_profit"].includes(kpi.key)
                            ? kpi.label.replace("Monthly", periodLabel)
                            : kpi.label;
                        const tone = kpi.key === "monthly_profit" ? (raw >= 0 ? "good" : "bad") : "default";
                        return <KpiCard key={kpi.key} label={label} value={value} tone={tone} />;
                    })}
                </div>
                {kpiCards.length === 0 ? <EmptyState text="No KPI cards are visible. Enable cards in Admin Settings > Dashboard Builder." /> : null}
            </SectionCard>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <SectionCard title={i18n.t("batch3.1fc0f8756bf3")} testid="dashboard-livestock-section">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card className="p-4">
                            <div className="tiny-label mb-2">{i18n.t("batch3.14467753cedc")}</div>
                            <ChartViewport height={220}>
                                <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                    <PieChart>
                                        <Pie data={livestock.animals_by_species || []} dataKey="count" nameKey="name" outerRadius={78} label>
                                            {(livestock.animals_by_species || []).map((_, idx) => (
                                                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartViewport>
                            {!(livestock.animals_by_species || []).length ? <EmptyState text="No species data." /> : null}
                        </Card>

                        <Card className="p-4">
                            <div className="tiny-label mb-2">{i18n.t("batch3.ce85fb7cc24b")}</div>
                            <ChartViewport height={220}>
                                <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                    <BarChart data={livestock.animals_by_status || []}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="status" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#2D6A4F" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartViewport>
                            {!(livestock.animals_by_status || []).length ? <EmptyState text="No status data." /> : null}
                        </Card>

                        <Card className="p-4 lg:col-span-2">
                            <div className="tiny-label mb-2">{i18n.t("batch3.f9e6fe6b07f8")}</div>
                            <ChartViewport height={240}>
                                <ResponsiveContainer width="100%" height={240} minWidth={0}>
                                    <LineChart data={livestock.weight_trend || []}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="period" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="avg_weight" stroke="#2A9D8F" strokeWidth={2} dot={{ r: 2 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </ChartViewport>
                            {!(livestock.weight_trend || []).length ? <EmptyState text="No weight trend data for selected period." /> : null}
                        </Card>

                        <Card className="p-4">
                            <div className="tiny-label mb-2">{i18n.t("batch3.1d70cc92c0d7")}</div>
                            <div className="text-2xl font-bold text-primary">{number((livestock.pregnant_due_soon || []).length)}</div>
                            {(livestock.pregnant_due_soon || []).slice(0, 5).map((row) => (
                                <div key={row.id} className="text-xs text-muted-foreground mt-1">
                                    {(row.animal_tag || "Animal")} {i18n.t("batch3.f6638aaa02f9")} {row.due_date}
                                </div>
                            ))}
                            {!(livestock.pregnant_due_soon || []).length ? <EmptyState text="No due-soon pregnancies." /> : null}
                        </Card>

                        <Card className="p-4">
                            <div className="tiny-label mb-2">{i18n.t("batch3.14210f551fd8")}</div>
                            <div className="text-2xl font-bold">{number((livestock.expected_farrowing || []).length)}</div>
                            {(livestock.expected_farrowing || []).slice(0, 5).map((row) => (
                                <div key={row.id} className="text-xs text-muted-foreground mt-1">
                                    {(row.animal_tag || "Animal")} - {row.days_until} {i18n.t("batch3.1e0b597ec3c9")}
                                </div>
                            ))}
                            {!(livestock.expected_farrowing || []).length ? <EmptyState text="No expected farrowing rows." /> : null}
                        </Card>

                        <Card className="p-4 lg:col-span-2">
                            <div className="tiny-label mb-2">{i18n.t("batch3.985cefcf2186")}</div>
                            <ChartViewport height={220}>
                                <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                    <LineChart data={livestock.mortality_trend || []}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="period" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="count" stroke="#E76F51" strokeWidth={2} dot={{ r: 2 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </ChartViewport>
                            {!(livestock.mortality_trend || []).length ? <EmptyState text="No mortality events in selected period." /> : null}
                        </Card>

                        <Card className="p-4 lg:col-span-2">
                            <div className="tiny-label mb-2">{i18n.t("batch3.bd7d7de86af6")}</div>
                            {(livestock.market_ready_animals || []).slice(0, 8).map((row) => (
                                <div key={row.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                                    <div className="text-sm font-medium">{row.tag_id || row.name || row.id}</div>
                                    <div className="text-xs text-muted-foreground">{row.species_name || row.status || "-"}</div>
                                </div>
                            ))}
                            {!(livestock.market_ready_animals || []).length ? <EmptyState text="No market-ready animals." /> : null}
                        </Card>
                    </div>
                </SectionCard>

                <SectionCard title={i18n.t("batch3.5674f7dce889")} testid="dashboard-financial-section">
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <KpiCard label={i18n.t("batch3.1c89b1f2aaab")} value={money((financial.income_vs_expenses || {}).income || 0)} tone="good" />
                        <KpiCard label={i18n.t("batch3.8d437dbf95ab")} value={money((financial.income_vs_expenses || {}).expenses || 0)} tone="bad" />
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        <Card className="p-4">
                            <div className="tiny-label mb-2">{i18n.t("batch3.33399e1e8956")}</div>
                            <ChartViewport height={240}>
                                <ResponsiveContainer width="100%" height={240} minWidth={0}>
                                    <BarChart data={financial.profit_trend || []}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="period" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip formatter={(v) => money(v)} />
                                        <Legend />
                                        <Bar dataKey="income" fill="#2D6A4F" radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="expense" fill="#A95C3E" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartViewport>
                            {!(financial.profit_trend || []).length ? <EmptyState text="No income and expense trend data." /> : null}
                        </Card>

                        <Card className="p-4">
                            <div className="tiny-label mb-2">{i18n.t("batch3.285420aea9e2")}</div>
                            <ChartViewport height={220}>
                                <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                    <LineChart data={financial.profit_trend || []}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="period" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip formatter={(v) => money(v)} />
                                        <Line type="monotone" dataKey="profit" stroke="#1D4ED8" strokeWidth={2} dot={{ r: 2 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </ChartViewport>
                            {!(financial.profit_trend || []).length ? <EmptyState text="No profit trend data in selected period." /> : null}
                        </Card>

                        <Card className="p-4">
                            <div className="tiny-label mb-2">{i18n.t("batch3.9644346d2301")}</div>
                            <ChartViewport height={220}>
                                <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                    <BarChart data={financial.sales_trend || []}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="period" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip formatter={(v) => money(v)} />
                                        <Bar dataKey="sales" fill="#2A9D8F" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartViewport>
                            {!(financial.sales_trend || []).length ? <EmptyState text="No sales in selected period." /> : null}
                        </Card>

                        <Card className="p-4">
                            <div className="tiny-label mb-2">{i18n.t("batch3.444fa32c06a7")}</div>
                            <ChartViewport height={220}>
                                <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                    <PieChart>
                                        <Pie data={financial.expense_categories || []} dataKey="amount" nameKey="category" outerRadius={78} label>
                                            {(financial.expense_categories || []).map((_, idx) => (
                                                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(v) => money(v)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartViewport>
                            {!(financial.expense_categories || []).length ? <EmptyState text="No expense categories in selected period." /> : null}
                        </Card>
                    </div>
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <SectionCard title={i18n.t("batch3.226da5b84ad7")} testid="dashboard-inventory-section">
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <KpiCard label={i18n.t("batch3.a2db98ef530b")} value={money(inventory.valuation || 0)} />
                        <KpiCard
                            label={i18n.t("batch3.9cc8dedc2b90")}
                            value={`${number((inventory.feed_stock || {}).count || 0)} / ${number((inventory.medicine_stock || {}).count || 0)}`}
                            sub="Feed items / Medicine items"
                        />
                    </div>

                    <Card className="p-4 mb-4">
                        <div className="tiny-label mb-2">{i18n.t("batch3.88f98b3800dc")}</div>
                        <ChartViewport height={220}>
                            <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                <LineChart data={inventory.feed_consumption_trend || []}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                    <XAxis dataKey="period" fontSize={10} />
                                    <YAxis fontSize={10} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="consumed_qty" stroke="#E9C46A" strokeWidth={2} dot={{ r: 2 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartViewport>
                        {!(inventory.feed_consumption_trend || []).length ? <EmptyState text="No feed consumption activity in selected period." /> : null}
                    </Card>

                    <Card className="p-4 mb-4">
                        <div className="tiny-label mb-2">{i18n.t("batch3.b956fe74ee95")}</div>
                        <ChartViewport height={220}>
                            <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                <BarChart data={inventory.inventory_movement_summary || []}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                    <XAxis dataKey="period" fontSize={10} />
                                    <YAxis fontSize={10} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="stock_in" fill="#2D6A4F" radius={[6, 6, 0, 0]} />
                                    <Bar dataKey="stock_out" fill="#A95C3E" radius={[6, 6, 0, 0]} />
                                    <Bar dataKey="adjustment" fill="#577590" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartViewport>
                        {!(inventory.inventory_movement_summary || []).length ? <EmptyState text="No inventory movements in selected period." /> : null}
                    </Card>

                    <Card className="p-4 mb-4">
                        <div className="tiny-label mb-2">{i18n.t("batch3.869bd1fe0a00")}</div>
                        {(inventory.low_stock_items || []).slice(0, 8).map((item) => (
                            <div key={item.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                                <div className="text-sm font-medium">{item.name || "Item"}</div>
                                <div className="text-xs text-muted-foreground">{item.quantity} {item.unit || item.uom || "unit"}</div>
                            </div>
                        ))}
                        {!(inventory.low_stock_items || []).length ? <EmptyState text="No low-stock inventory items." /> : null}
                    </Card>

                    <Card className="p-4 mb-4">
                        <div className="tiny-label mb-2">{i18n.t("batch3.06cdec0f2e09")}</div>
                        {(inventory.expiring_items || []).slice(0, 8).map((item) => (
                            <div key={item.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                                <div className="text-sm font-medium">{item.name || "Item"}</div>
                                <div className="text-xs text-muted-foreground">{item.expiry_date || "No date"}</div>
                            </div>
                        ))}
                        {!(inventory.expiring_items || []).length ? <EmptyState text="No expiring inventory items." /> : null}
                    </Card>

                    <Card className="p-4">
                        <div className="tiny-label mb-2">{i18n.t("batch3.cfc77c0c1b1f")}</div>
                        {(inventory.recent_stock_movements || []).slice(0, 10).map((mv) => (
                            <div key={mv.id} className="grid grid-cols-12 gap-2 py-1.5 border-b last:border-0 text-xs">
                                <div className="col-span-5 truncate">{mv.item_name || "Inventory item"}</div>
                                <div className="col-span-3 text-muted-foreground truncate">{mv.type || "movement"}</div>
                                <div className="col-span-2 text-right">{mv.quantity}</div>
                                <div className="col-span-2 text-right text-muted-foreground">{mv.date || "-"}</div>
                            </div>
                        ))}
                        {!(inventory.recent_stock_movements || []).length ? <EmptyState text="No stock movement history." /> : null}
                    </Card>
                </SectionCard>

                <SectionCard title={i18n.t("batch3.eb6360a6e8b7")} testid="dashboard-attendance-section">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <KpiCard label={i18n.t("batch3.05882967d7f8")} value={number(attendance.present_today || 0)} tone="good" />
                        <KpiCard label={i18n.t("batch3.9e9477eabd86")} value={number(attendance.absent_today || 0)} tone="bad" />
                        <KpiCard label={i18n.t("batch3.317b1560fa04")} value={number(attendance.late_today || 0)} />
                        <KpiCard label={i18n.t("batch3.0eb25859933e")} value={number(attendance.active_employees || 0)} />
                        {attendance.payroll_kpi ? (
                            <KpiCard
                                label={attendance.payroll_kpi.label || "Payroll Preview (Gross)"}
                                value={money(attendance.payroll_kpi.value || 0)}
                            />
                        ) : null}
                    </div>

                    <Card className="p-4 mt-4">
                        <div className="tiny-label mb-2">{i18n.t("batch3.8324113fd693")}</div>
                        <ChartViewport height={220}>
                            <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                <BarChart data={attendance.attendance_summary || []}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                    <XAxis dataKey="period" fontSize={10} />
                                    <YAxis fontSize={10} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="present" fill="#2D6A4F" radius={[6, 6, 0, 0]} />
                                    <Bar dataKey="late" fill="#E9C46A" radius={[6, 6, 0, 0]} />
                                    <Bar dataKey="absent" fill="#E76F51" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartViewport>
                        {!(attendance.attendance_summary || []).length ? <EmptyState text="No attendance records in selected period." /> : null}
                    </Card>
                </SectionCard>
            </div>

            <SectionCard
                title={i18n.t("batch3.7ed9b8236e54")}
                right={
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {number((alerts.counts || {}).total || 0)} {i18n.t("batch3.4053152f0642")}
                    </div>
                }
                testid="dashboard-alerts-section"
            >
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
                    <KpiCard label={i18n.t("batch3.b298bc6a9e73")} value={number(alerts.vaccinations_due || 0)} />
                    <KpiCard label={i18n.t("batch3.865c087f16fa")} value={number(alerts.treatments_due || 0)} />
                    <KpiCard label={i18n.t("batch3.5fa823f392c5")} value={number(alerts.expected_births || 0)} />
                    <KpiCard label={i18n.t("batch3.023d1181ebc6")} value={number(alerts.low_stock || 0)} tone={(alerts.low_stock || 0) > 0 ? "bad" : "default"} />
                    <KpiCard label={i18n.t("batch3.cce0c146af66")} value={number(alerts.expiring_medicine || 0)} tone={(alerts.expiring_medicine || 0) > 0 ? "bad" : "default"} />
                    <KpiCard label={i18n.t("batch3.af5c4843442e")} value={number(alerts.overdue_tasks || 0)} tone={(alerts.overdue_tasks || 0) > 0 ? "bad" : "default"} />
                </div>

                <Card className="p-4">
                    <div className="tiny-label mb-2">{i18n.t("batch3.016e578a2ed1")}</div>
                    {(alerts.top_alerts || []).slice(0, 12).map((alert) => (
                        <div key={alert.id} className="flex items-start justify-between gap-3 py-2 border-b last:border-0">
                            <div>
                                <div className="text-sm font-semibold">{alert.title || "Alert"}</div>
                                <div className="text-xs text-muted-foreground">{alert.detail || "No details"}</div>
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{alert.severity || "info"}</div>
                        </div>
                    ))}
                    {!(alerts.top_alerts || []).length ? <EmptyState text="No active alerts." /> : null}
                </Card>
            </SectionCard>

            <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                    <div className="flex items-center gap-2"><CalendarDaysIcon />{i18n.t("batch3.aa389b318ef8")} {dashboard?.period?.selected || "month"}</div>
                    <div className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" />{i18n.t("batch3.281119acb9e0")}</div>
                    <div className="flex items-center gap-2"><Activity className="w-4 h-4 text-primary" />{i18n.t("batch3.1c9f6939e013")}</div>
                    <div className="flex items-center gap-2"><TrendIcon profit={Number(kpis.monthly_profit || 0)} />{i18n.t("batch3.11dc9e195292")} {Number(kpis.monthly_profit || 0) >= 0 ? "Profitable" : "Negative margin"}</div>
                </div>
            </Card>

            <LegacyBanner />
        </div>
    );
}

function CalendarDaysIcon() {
    return <div className="w-4 h-4 rounded-sm bg-primary/20 border border-primary/30" />;
}

function TrendIcon({ profit }) {
    if (profit >= 0) {
        return <TrendingUp className="w-4 h-4 text-primary" />;
    }
    return <TrendingDown className="w-4 h-4 text-destructive" />;
}
